import React, { useState } from 'react';
import useDesignerStore from './designerStore';
import { ZoomIn, ZoomOut, ArrowCounterclockwise, ArrowClockwise } from 'react-bootstrap-icons';
import { createCard, updateCard, getCardById } from './cardService';
import { cardSizes } from './cardSizes';
import exportCurrentSurface from './DesignerStage';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiFetch } from '../../api/api';

// import './Buttons.css';
import '../../styles/Buttons.css';

const zoomControls = {
  container: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '10px',
    color: 'white',
  },
  button: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    color: 'white',
    fontSize: '27px'
  }
};


// Small helper to get active org id consistently
const getActiveOrgId = () => {
  if (typeof window === 'undefined') return '';
  return (
    localStorage.getItem('activeOrgId') ||
    localStorage.getItem('activeOrg') ||
    ''
  );
};

const Toolbar = () => {
  const { activeTool, selectedIds, setSelectedIds } = useDesignerStore();
  const navigate = useNavigate();
  const setActiveTool = useDesignerStore(state => state.setActiveTool);
  const addObject = useDesignerStore(state => state.addObject);
  const undo = useDesignerStore(state => state.undo);
  const redo = useDesignerStore(state => state.redo);
  const isAspectRatioLocked = useDesignerStore(state => state.isAspectRatioLocked);
  const setAspectRatioLock = useDesignerStore(state => state.setAspectRatioLock);
  const bringForward = useDesignerStore(state => state.bringForward);
  const sendBackward = useDesignerStore(state => state.sendBackward);
  const moveToTop = useDesignerStore(state => state.moveToTop);
  const moveToBottom = useDesignerStore(state => state.moveToBottom);
  const isSnapToGridEnabled = useDesignerStore(state => state.isSnapToGridEnabled);
  const showGrid = useDesignerStore(state => state.showGrid);
  const toggleSnapToGrid = useDesignerStore(state => state.toggleSnapToGrid);
  const toggleGrid = useDesignerStore(state => state.toggleGrid);
  const [cardId, setCardId] = useState(null);
  const scale = useDesignerStore(state => state.scale);
  const setScale = useDesignerStore(state => state.setScale);
  const loadCard = useDesignerStore(state => state.loadCard);
  const resetCard = useDesignerStore(state => state.resetCard);
  const activeSurface = useDesignerStore(s => s.activeSurface);
  const setActiveSurface = useDesignerStore(s => s.setActiveSurface);
  const cardSettings = useDesignerStore(s => s.cardSettings);
  const isDirty = useDesignerStore(s => s.isDirty);
  const exportFn = useDesignerStore(s => s.exportCurrentSurface);

  const getSurfaceOptions = () => {
    const sides = cardSettings?.sides || 'Simplex';
    const silverFront = cardSettings?.silverFront === 'yes';
    const silverBack = cardSettings?.silverBack === 'yes';

    const options = [{ key: 'front', label: 'Card front' }];

    if (silverFront) options.push({ key: 'frontSilver', label: 'Card front - silver' });

    if (sides === 'Duplex') {
      options.push({ key: 'back', label: 'Card back' });
      if (silverBack) options.push({ key: 'backSilver', label: 'Card back - silver' });
    }

    return options;
  };

  const handleAddPhotoFrame = () => {
    const store = useDesignerStore.getState();
    const { cardSettings } = store;
    const base = cardSizes[cardSettings.size] || { width: 1012, height: 638 };
    const cardW = cardSettings.format === 'portrait' ? base.width : base.height;
    const cardH = cardSettings.format === 'portrait' ? base.height : base.width;

    const obj = {
      id: Date.now().toString(),
      type: 'photo',
      x: 80,
      y: 80,
      width: Math.round(cardW * 0.6),
      height: Math.round(cardH * 0.6),
      photoMode: 'fit',          // 'background' | 'fit' | 'fill'
      stroke: '#0099FF',
      strokeWidth: 2,
      dash: [8, 6],
      cornerRadius: 12,
      opacity: 1,
      draggable: true,
      rotation: 0,
    };
    useDesignerStore.getState().addCapturedPhotoInput(obj);
    setSelectedIds([obj.id]);
  };

  const handleShapesClick = (tool) => {
    setSelectedIds([]);
    setActiveTool(tool);
  };



  const cancel = async () => {
    const { cardId } = useDesignerStore.getState();
    if (!cardId) return;
    resetCard();
    try {
      const card = await getCardById(cardId);
      loadCard(card); // ✅ always pass full backend card
    } catch (error) {
      console.error("Error loading card:", error);
    }
  };


  const handleAddText = (tool) => {
    const textObject = {
      id: Date.now().toString(),
      type: 'text',
      x: 100,
      y: 100,
      text: 'New Text',
      fontSize: 24,
      fontFamily: 'Arial',
      fill: '#000000',
      draggable: true,
      rotation: 0,
    };
    setActiveTool(tool);
    addObject(textObject);
    setSelectedIds([textObject.id]);
  };

  const animateZoom = (targetScale) => {
    const duration = 200;
    const start = performance.now();
    const initialScale = useDesignerStore.getState().scale;

    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = progress * (2 - progress);
      const currentScale = initialScale + (targetScale - initialScale) * eased;
      useDesignerStore.getState().setScale(currentScale);
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  const handleReturn = () => {
    if (!isDirty) {
      navigate('/cards');
      return;
    }

    toast.warn(
      <div>
        <div style={{ marginBottom: 8 }}>
          You have unsaved changes. Leave without saving?
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-red"
            onClick={() => {
              toast.dismiss();
              navigate('/cards');
            }}
          >
            Leave
          </button>
          <button
            className="btn-gray"
            onClick={() => toast.dismiss()}
          >
            Stay
          </button>
        </div>
      </div>,
      { autoClose: false }
    );
  };

  const exportPreviewBlob = async () => {
    // preferred: add this to store/designer and return a Blob
    if (typeof exportFn === 'function') {
      const result = await exportFn('png', { mode: 'blob', surface: activeSurface });
      if (result instanceof Blob) return result;
    }

    throw new Error('exportFn does not return a Blob yet');
  };

  const blobToFile = (blob, name = 'card-preview.png') => {
    return new File([blob], name, { type: blob.type || 'image/png' });
  };



  const uploadBlobToS3 = async ({ blob, orgId, cardId }) => {
    const file = blobToFile(blob, `card_${cardId || 'new'}_${Date.now()}.png`);

    const { uploadUrl, bucket, key } = await apiFetch('/media/upload-url', {
      method: 'POST',
      body: {
        mimeType: file.type,
        originalName: file.name
      }
    });

    await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type
      },
      body: file
    });

    const publicUrl = `https://${bucket}.s3.amazonaws.com/${key}`;

    return {
      publicUrl,
      bucket,
      key,
      mimeType: file.type,
      originalName: file.name,
      size: file.size
    };
  };

  const handleSave = async () => {
    const store = useDesignerStore.getState();
    const orgId = getActiveOrgId();
    toast.info('BE PATIENT! Card updating...');

    try {
      store.validateCapturePhotoInput();
      let previewInfo = null;

      const previewBlob = await exportFn('png');


      if (previewBlob instanceof Blob) {
        previewInfo = await uploadBlobToS3({
          blob: previewBlob,
          orgId,
          cardId: store.cardId
        });
      } else {
        console.warn("❌ export did not return a blob");
      }

      const basePayload = store.getCardPayload();


      const payload = {
        ...basePayload,
        orgId,
        cardImage: previewInfo?.publicUrl || basePayload.cardImage || '',
        cardImageKey: previewInfo?.key || '',
        cardImageBucket: previewInfo?.bucket || ''
      };


      if (store.cardId) {

        await updateCard(store.cardId, payload, orgId);
        store.setIsDirty(false);
        toast.success('Card updated!');
      } else {
        const result = await createCard(payload, orgId);
        store.setCardId(result.cardId || result._id);
        store.setIsDirty(false);
        toast.success('Card saved!');
      }

    } catch (err) {
      console.error(err);
      toast.error(err?.code === 'CAPTURE_FRAME_REQUIRED' ? err.message : 'Save failed');
    }
  };

  return (
    <div className="toolbar-row">
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ fontSize: '.7rem' }}>View:</span>
          <select
            value={activeSurface}
            onChange={(e) => setActiveSurface(e.target.value)}
            style={{
              fontSize: '.7rem',
              height: '22px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: 'white',
              padding: '0 6px',
            }}
            title="Select which surface you are designing"
          >
            {getSurfaceOptions().map(opt => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
        </select>
      </div>
      <button className="btn-white soft" onClick={() => handleShapesClick('info')} style={{ marginLeft: "20px" }}>Settings</button>
      <button className="btn-white soft" onClick={() => handleShapesClick('shapes')}>Shapes</button>
      <button className="btn-white soft" onClick={() => handleShapesClick('text')}>Text</button>
      <button className="btn-white soft" onClick={() => handleShapesClick('images')}>Images</button>
      <button className="btn-white soft" onClick={() => handleShapesClick('photo')}>Photo</button>
      <button className="btn-white soft" onClick={() => handleShapesClick('qrcode')}>QR Code</button>
      <div className="toolbar-separator"></div>
      <button className="btn-white soft" onClick={() => exportFn?.('png')}>
        Download
      </button>
      <div className="toolbar-separator"></div>
      <button onClick={() => undo()} style={{ cursor: "pointer", height:"20px", border: "thin #ccc solid", borderRadius: "4px", padding: "3px 2px 0px 2px", backgroundColor: "white" }}>
        <ArrowCounterclockwise size={14} />
      </button>
      <button onClick={() => redo()} style={{ cursor: "pointer", height:"20px", border: "thin #ccc solid", borderRadius: "4px", padding: "3px 2px 0px 2px", backgroundColor: "white" }}>
        <ArrowClockwise size={14} />
      </button>
      <button onClick={() => animateZoom(scale * 1.1)} style={{ cursor: "pointer", height:"20px", border: "thin #ccc solid", borderRadius: "4px", padding: "3px 2px 0px 2px", backgroundColor: "white" }}>
        <ZoomIn size={14} />
      </button>
      <button onClick={() => animateZoom(scale * 0.9)} style={{ cursor: "pointer", height:"20px", border: "thin #ccc solid", borderRadius: "4px", padding: "3px 2px 0px 2px", backgroundColor: "white" }}>
        <ZoomOut size={14} />
      </button>
      <div className="toolbar-separator"></div>
      <div className="separator"></div>

      <button className="btn-save" onClick={handleSave}>Save</button>
      <button className="btn-cancel" onClick={cancel}>Cancel</button>
      <button className="btn-return" onClick={handleReturn}>Return</button>


    </div>
  );
};

export default Toolbar;
